/**
 * OTP Modal
 * @description This component will generate and verify OTP within a modal. This component doesn't rely on it's own unique API instead the component which is loading this modal is injectin a payload in the following format
 *  Example ::
 *
    const payload = {
      values: {request: values}, // form values to be sent with POST request while generating or verifying the OTP
      forwardAction: addBankAccount, // Action to triggered when OTP is verified
      csrfRequestIdentifier: auth.csrfRequestIdentifier, // Required
      api: {
        generate: API.bank.generateOTP, // Generat OTP API
        verify: API.bank.verifyOTP, // Verify OTP API
      },
    };

    // Request to open OTP modal
    openModal('OTPModal', {payload}); // Finally open the OTP modal with payload
 *
 * @author Tirumal Rao
 */
import React, { Component } from 'react';
import { Modal, Message } from 'semantic-ui-react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Form, Button, Loader, Segment } from 'semantic-ui-react';
import { closeModal } from '../../modals/modalActions';
import { deleteOTP, generateOTP, verifyOTP } from '../OTPactions';
import { toastr } from 'react-redux-toastr';

/**
 * CONFIGURATIONS START
 */

// Redux actions
const actions = {
  closeModal,
  deleteOTP,
  generateOTP,
  verifyOTP
};

// Redux state
const mapState = state => ({
  auth: state.auth,
  async: state.async,
  otp: (state.otp && state.otp.data) || {},
  otpError: state.otp && state.otp.error
});

/**
 * CONFIGURATIONS ENDS
 */

class OTPModal extends Component {
  static propTypes = {
    async: PropTypes.object.isRequired,
    auth: PropTypes.object.isRequired,
    closeModal: PropTypes.func.isRequired,
    deleteOTP: PropTypes.func.isRequired,
    generateOTP: PropTypes.func.isRequired,
    verifyOTP: PropTypes.func.isRequired,
    payload: PropTypes.object.isRequired,
    otp: PropTypes.object,
    otpError: PropTypes.object
  };

  constructor(props) {
    super(props);

    this.first = '';
    this.second = '';
    this.third = '';
    this.fourth = '';
  }

  // init state
  state = { first: '', second: '', third: '', fourth: '' };

  // Generate OTP
  genOTP = async () => {
    const {
      auth,
      generateOTP,
      payload: { api, values },
    } = this.props;

    await generateOTP(api.generate, values, auth.csrfRequestIdentifier);
    console.log('d');
    this.setState({
      first: '',
      second: '',
      third: '',
      fourth: ''
    });
  };

  // Validate OTP
  validateOTP = async () => {
    const {
      auth,
      payload: { api, forwardAction, values, verifyData },
      otp,
      verifyOTP
    } = this.props;

    let o = '';

    o =
      (this.state.first || '') +
      (this.state.second || '') +
      (this.state.third || '') +
      (this.state.fourth || '');

    const data = {
      otpId: otp.otpId,
      otp: o,
      ...verifyData,
      request: values.request
    };

    try {
      if (o !== '0000' && o.length === 4) {
        // Verify OTP
        const otpResponse = await verifyOTP(
          api.verify,
          data,
          this.props.payload,
          forwardAction,
          auth.csrfRequestIdentifier,
          auth.migration && auth.migration.walletToken
        );
        if (!otpResponse.transactionId) {
          if (otpResponse.error || otpResponse.errorMessage) {
            toastr.error('OTP Verification', `Error while verifying OTP! Status Code: ${otpResponse.statusCode}`);
          } else {
            toastr.success('Successfully Updated!');
          }
        }
      } else {
        toastr.error('OTP Error', 'Invalid OTP!');
      }
    } catch (e) {
      toastr.error('Error', 'Server error!');
    }


  };

  // Handle OTP Field Change
  handleOTPChange = event => {
    const trgName = event.target.name;
    const trgVal = event.target.value;
    let setFocus = '';

    if (trgVal.toString().length === 1) {
      switch (trgName) {
        case 'first':
          setFocus = this.second;
          break;
        case 'second':
          setFocus = this.third;
          break;
        case 'third':
          setFocus = this.fourth;
          break;
        default:
          setFocus = this.first;
      }

      if (setFocus.focus && typeof setFocus.focus === 'function')
        setFocus.focus();
    }

    this.setState({ [trgName]: trgVal });
  };

  handleResendOTP = (e) => {
    e.preventDefault();
    this.genOTP();
  }

  render() {
    const { async, closeModal, otp, otpError } = this.props;

    return (
      <Modal closeIcon={true} size="tiny" open={true} onClose={closeModal}>
        <Modal.Header>Verify OTP</Modal.Header>
        <Modal.Content>
          <Modal.Description>
            <Segment className="no-border-segment" textAlign="center">
              You will get an OTP on your registered mobile number{' '}
              {otp.maskedMobileNumber || ''}
              <Loader
                active={async.key === 'generateOTP' && async.loading}
                size="tiny"
                inline
                className="inlineLoaderModal"
              />
            </Segment>

            {otpError && otpError.errorMessage && (
              <Message negative>
                <Message.Header>Error: {otpError.errorMessage}</Message.Header>
              </Message>
            )}

            {!(otpError && otpError.errorMessage) && (
              <Form
                className="otp"
                onSubmit={this.validateOTP}
                loading={async.key === 'verifyOTP' && async.loading}
              >
                <Segment className="no-border-segment" textAlign="center">
                  <input
                    ref={input => {
                      this.first = input;
                    }}
                    value={this.state.first}
                    name="first"
                    autoComplete="off"
                    placeholder=""
                    type="tel"
                    onChange={this.handleOTPChange}
                    maxLength="1"
                  />

                  <input
                    ref={input => {
                      this.second = input;
                    }}
                    value={this.state.second}
                    name="second"
                    autoComplete="off"
                    placeholder=""
                    type="tel"
                    onChange={this.handleOTPChange}
                    maxLength="1"
                  />

                  <input
                    ref={input => {
                      this.third = input;
                    }}
                    value={this.state.third}
                    name="third"
                    autoComplete="off"
                    placeholder=""
                    type="tel"
                    onChange={this.handleOTPChange}
                    maxLength="1"
                  />

                  <input
                    ref={input => {
                      this.fourth = input;
                    }}
                    value={this.state.fourth}
                    name="fourth"
                    autoComplete="off"
                    placeholder=""
                    type="tel"
                    onChange={this.handleOTPChange}
                    maxLength="1"
                  />
                </Segment>

                <Segment className="no-border-segment" textAlign="center">
                  <p>Didn't receive the OTP yet?</p>
                  <Button className="buttonLink" onClick={this.handleResendOTP}>Resend OTP</Button>
                </Segment>

                <Segment className="no-border-segment" textAlign="center">
                  <Button className="fcbtn fcprimary small" type="submit">
                    SUBMIT
                  </Button>
                </Segment>
              </Form>
            )}
          </Modal.Description>
        </Modal.Content>
      </Modal>
    );
  }

  componentWillUnmount() {
    // delete the OTP from app state when component unmounts
    this.props.deleteOTP();
  }

  componentDidMount() {
    const { generateOTPonLoad } = this.props.payload;

    if (generateOTPonLoad) {
      this.genOTP();
    }
  }
}

// Redux connect and reduxForm HOC on statefull component 'PromoCodeModal'
export default connect(
  mapState,
  actions
)(OTPModal);
